import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import qs from 'qs'
import { connect } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import Loader from '~components/Loader'
import { setColourSwatches } from '~redux/shop'
import Card from '~components/Card'
import Heading from '~components/brands/Heading'
import Pagination from '~components/ui/Pagination'
import { pageView } from '~helpers/ga'
import { removeEmptyObjectKeys } from '~helpers/main'

const ShopBrand = ({ setColourSwatches, brands }) => {
  const [loading, setLoading] = useState(false)
  const [seasons, setSeasons] = useState(null)
  const [filters, setFilters] = useState({ season: { selected: '', options: [] } })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const listingsRef = useRef()
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const cancelTokenSource = axios.CancelToken.source()
  const brand = brands.find(b => b.slug === params.brand)

  useEffect(() => {
    pageView()
    initializeFiltersAndLoadSeasons()
  }, [params.brand, location.search])

  const initializeFiltersAndLoadSeasons = () => {
    const { page: queryPage, season: querySeason } = qs.parse(location.search, { ignoreQueryPrefix: true })
    const pageToLoad = parseInt(queryPage) || 1
    if (querySeason) updateSelectedSeasonFilter(querySeason)
    fetchSeasons(pageToLoad)
  }

  const fetchSeasons = async (page = 1) => {
    if (loading) return
    setLoading(true)

    try {
      const queryParams = { page, season: filters.season.selected }

      const { data } = await axios.get(`/api/brands/${params.brand}/seasons`, {
        params: removeEmptyObjectKeys(queryParams),
        cancelToken: cancelTokenSource.cancel()
      })

      updateStateWithSeasonData(data)
      updateUrlParams(data.seasons.current_page, filters.season.selected)
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching seasons:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  const updateStateWithSeasonData = (data) => {
    setSeasons(data.seasons.data)
    setPage(data.seasons.current_page)
    setTotalPages(data.seasons.last_page)
    setColourSwatches(data.colourSwatches)
    updateSeasonOptions(data.seasonOptions)
  }

  const updateSeasonOptions = (options) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      season: {
        ...prevFilters.season,
        options
      }
    }))
  }

  const updateUrlParams = (page, season) => {
    const queryParams = new URLSearchParams(location.search)
    page > 1 ? queryParams.set('page', page) : queryParams.delete('page')
    season ? queryParams.set('season', season) : queryParams.delete('season')
    navigate(`?${queryParams.toString()}`, { replace: true })
  }

  const updateSelectedSeasonFilter = (season) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      season: {
        ...prevFilters.season,
        selected: season
      }
    }))
  }

  const handleSeasonFilterChange = (e) => {
    const newSeason = e.target.value
    updateSelectedSeasonFilter(newSeason)
    updateUrlParams(1, newSeason) // Reset to page 1 when the season changes
  }

  const handlePageChange = (newPage) => {
    if (totalPages >= newPage && newPage > 0 && newPage !== page) {
      updateUrlParams(newPage, filters.season.selected)
      scrollToTop()
    }
  }

  const scrollToTop = () => {
    const listings = listingsRef.current
    window.scrollTo({ top: listings ? listings.offsetTop : 0, behavior: 'smooth' })
  }

  const renderSeasonOptions = () => {
    return filters.season.options.map(item => ({
      name: item.replace(/SS/g, 'Spring/Summer ').replace(/AW/g, 'Autumn/Winter '),
      value: item
    }))
  }

  if (!seasons || !brand) {
    return <Loader />
  }

  return (
    <>
      <Heading
        breadcrumbs={[{ title: 'Shop', url: '/shop' }]}
        title={brand.name}
        filter={{
          placeholder: 'All seasons',
          items: renderSeasonOptions(),
          value: filters.season.selected,
          onChange: handleSeasonFilterChange
        }}
        className="mt-2 lg:mt-6 xxl:mt-10"
      />

      <div className="w-full" ref={listingsRef}>
        {Object.keys(seasons).map(season => (
          <section key={season} className="px-4 mx-auto mt-6 xxl:container md:px-8 xxl:px-0 mb-15 xxl:my-20">
            <h2 className="mb-4 md:mb-8 xxl:mb-10 lg:text-sm">
              {season.replace(/SS/g, 'Spring/Summer ').replace(/AW/g, 'Autumn/Winter ')}
            </h2>

            <div className="flex flex-wrap -mx-1 -my-5 md:-mx-2 lg:-mx-4 xl:-mx-5 xl:-my-10">
              {seasons[season].map(range => {
                const mainImage = range.images.find(i => i.is_thumbnail)
                return (
                  <div key={`${range.slug}_${range.colour_code}`} className="w-1/2 px-1 my-4 md:w-1/3 xl:w-1/4 md:px-2 lg:px-4 xl:px-5 xl:my-6">
                    <Card
                      image={{ src: mainImage ? mainImage.url_standard : null, alt: `${range.name} - ${range.colour}` }}
                      subtitle={brand.name}
                      title={range.name}
                      url={`/shop/ranges/${range.slug}?colour=${range.colour_code}`}
                      colour={range.colour}
                      styleCode={range.colour_code}
                    />
                  </div>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      <Pagination
        totalPages={totalPages}
        onClick={handlePageChange}
        currentPage={page}
        className="w-full mb-10 xl:my-20"
      />
    </>
  )
}

ShopBrand.propTypes = {
  brands: PropTypes.array.isRequired,
  setColourSwatches: PropTypes.func.isRequired
}

const mapStateToProps = (state) => ({
  brands: state.shop.brands,
  colourSwatches: state.shop.colourSwatches
})

const mapDispatchToProps = { setColourSwatches }

export default connect(mapStateToProps, mapDispatchToProps)(ShopBrand)
